import { useMemo, useState, type FormEvent } from 'react'
import { Check, Loader2, UserPlus } from 'lucide-react'
import { Modal, Field } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import { useLoad } from '@/features/administration/hooks'
import { api } from '@/shared/api/client'
import { cn } from '@/lib/utils'
import type { Country } from 'react-phone-number-input'
import { PhoneInput, phoneIsValid, countryOf, DEFAULT_COUNTRY } from '@/components/erp/phone-input'
import { InviteLinkPanel } from './InviteLinkPanel'
import { createUser, issueInvitation } from './api'
import { createMobilizer } from '@/features/mobilizers/api'
import { ROLES } from '@/features/mobilizers/vocab'
import { UnitPicker } from '@/features/candidacy/UnitPicker'
import type { MobilizerRoleCode } from '@/shared/api/types'
import type {
  InvitationIssuedResponse, PageResponse, RoleResponse, UserResponse,
} from '@/shared/api/types'

const SCOPE_LEVELS = ['NATIONAL', 'COUNTY', 'CONSTITUENCY', 'WARD', 'STATION']
const CLEARANCES = ['PUBLIC', 'INTERNAL', 'RESTRICTED', 'SECRET', 'TOP_SECRET']

/**
 * Create a user, pick their roles, and invite them — in one pass.
 *
 * Roles at creation matter because a user with no role holds no authorities and
 * sees an empty application. The backend accepts `roleIds` on create and runs the
 * escalation guard, so a caller cannot grant a role carrying a permission they do
 * not hold themselves; that refusal surfaces here as a 403 with its reason.
 *
 * Invitation is offered immediately after create because a new user is written
 * with `password_hash = NULL` and literally cannot sign in until one is issued.
 */
export function CreateUserDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  // E.164. The country is tracked alongside because the API takes the pair — see PhoneInput.
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [scopeLevel, setScopeLevel] = useState('NATIONAL')
  const [clearance, setClearance] = useState('INTERNAL')
  const [roleIds, setRoleIds] = useState<string[]>([])
  const [inviteNow, setInviteNow] = useState(true)

  /*
     The optional second half of onboarding.

     A system user and a mobilizer are different records — an account signs in,
     a mobilizer profile is a person in a hierarchy with a unit, a supervisor and
     a role — and `mobilizer_profile.user_id` is the (UNIQUE) link between them.
     Creating them separately means typing the same person's name and number
     twice and remembering to connect them, which is how a field team ends up
     with accounts nobody can find on the map.

     Optional because not every account is a field worker: an analyst in the
     office has no unit and should not be forced to invent one.
  */
  const [alsoMobilizer, setAlsoMobilizer] = useState(false)
  const [mobRole, setMobRole] = useState<MobilizerRoleCode>('MOBILIZER')
  const [mobUnitId, setMobUnitId] = useState<string | null>(null)
  const [mobNationalId, setMobNationalId] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [created, setCreated] = useState<
    { user: UserResponse; invite?: InvitationIssuedResponse } | null>(null)

  const roles = useLoad(
    () => api.get<PageResponse<RoleResponse>>('/rbac/roles?page=0&size=200&assignable=true'),
    [],
  )
  const assignable = useMemo(() => roles.data?.content ?? [], [roles.data])

  function reset() {
    setEmail(''); setName(''); setPhone(''); setCountry(DEFAULT_COUNTRY)
    setScopeLevel('NATIONAL'); setClearance('INTERNAL')
    setRoleIds([]); setInviteNow(true)
    setError(null); setFieldErrors({}); setCreated(null)
  }

  function close() { reset(); onClose() }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFieldErrors({})
    try {
      const user = await createUser({
        phoneCountry: countryOf(phone, country),
        phone,
        // Optional now. Empty string rather than undefined would ask the server to store "".
        email: email.trim() || undefined,
        name: name.trim(),
        scopeLevel,
        clearance,
        roleIds: roleIds.length ? roleIds : undefined,
      })
      let invite: InvitationIssuedResponse | undefined
      if (inviteNow) {
        // A failed invite must not read as a failed create — the user exists.
        try {
          invite = await issueInvitation(user.id)
        } catch (err) {
          setError(`User created, but the invitation could not be sent: ${explain(err)}`)
        }
      }
      // AFTER the account exists, and never instead of it. A failure here must
      // read as "the user was created, the profile was not" — exactly the rule
      // the invitation above follows — because the account is the thing that
      // cannot be redone without deleting a person.
      let mobilizerFailed: string | undefined
      if (alsoMobilizer && mobUnitId) {
        try {
          await createMobilizer({
            fullName: name.trim(),
            phoneCountry: countryOf(phone, country),
            phone,
            email: email.trim() || undefined,
            nationalId: mobNationalId.trim() || undefined,
            role: mobRole,
            primaryUnitId: mobUnitId,
            userId: user.id,
          })
        } catch (err) {
          mobilizerFailed = explain(err)
          setError(
            `User created, but the mobilizer profile was not: ${mobilizerFailed}`,
          )
        }
      }
      setCreated({ user, invite })
      onCreated()
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors())
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  // ── Result view ───────────────────────────────────────────────────────────
  if (created) {
    return (
      <Modal
        open={open}
        onClose={close}
        title="User created"
        description={`${created.user.name} · ${created.user.phone}`}
        footer={<button className="erp-btn erp-btn-primary" onClick={close}>Done</button>}
      >
        <div className="space-y-3">
          {error && <Note tone="bad">{error}</Note>}
          <Note tone="good">
            Account created with status <strong>{created.user.status}</strong>
            {roleIds.length > 0 && <> and {roleIds.length} role{roleIds.length === 1 ? '' : 's'}</>}.
          </Note>

          {created.invite
            ? <InviteLinkPanel
                result={created.invite}
                phone={created.user.phone}
                email={created.user.email}
              />
            : <Note tone="info">
                No invitation sent. This account cannot sign in until you issue one from
                the user&apos;s detail panel.
              </Note>}
        </div>
      </Modal>
    )
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onClose={close}
      title="Create user"
      description="Accounts start with no password. An invitation is what lets them in."
      width="lg"
      footer={
        <>
          <button type="button" className="erp-btn" onClick={close}>Cancel</button>
          <button
            type="submit"
            form="create-user-form"
            className="erp-btn erp-btn-primary"
            // The unit is part of the condition, not a silent skip. Ticking the
            // box and leaving the picker empty would otherwise create the account
            // and quietly drop the profile — the person would be told "User
            // created" and discover days later that they are on no map.
            disabled={
              busy ||
              !phoneIsValid(phone) ||
              !name.trim() ||
              (alsoMobilizer && !mobUnitId)
            }
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
            Create user
          </button>
        </>
      }
    >
      <form id="create-user-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" required error={fieldErrors.name}>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   className="erp-input w-full" required />
          </Field>
          <Field label="Phone number" required error={fieldErrors.phone}>
            <PhoneInput
              value={phone}
              onChange={setPhone}
              country={country}
              onCountryChange={setCountry}
            />
            <p className="text-erp-xs text-muted-foreground mt-1">
              This is what they sign in with, and where the invitation is sent.
            </p>
          </Field>
          <Field label="Email" error={fieldErrors.email}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="erp-input w-full" placeholder="Optional" />
          </Field>
          <Field label="Clearance" error={fieldErrors.clearance}>
            <select value={clearance} onChange={(e) => setClearance(e.target.value)}
                    className="erp-input w-full">
              {CLEARANCES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
          <Field
            label="Scope level"
            hint="The unit picker arrives with Phase 2 geography; national scope until then."
            error={fieldErrors.scopeLevel}
          >
            <select value={scopeLevel} onChange={(e) => setScopeLevel(e.target.value)}
                    className="erp-input w-full">
              {SCOPE_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>

        <Field
          label="Roles"
          hint="A user with no role holds no permissions and sees an empty application."
        >
          <div className="border border-border rounded-md max-h-52 overflow-y-auto divide-y divide-border/60">
            {roles.loading && (
              <div className="px-3 py-3 text-erp-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-2" /> Loading roles…
              </div>
            )}
            {!roles.loading && assignable.length === 0 && (
              <div className="px-3 py-3 text-erp-sm text-muted-foreground">
                No assignable roles. Create one under Roles &amp; Permissions first.
              </div>
            )}
            {assignable.map((r) => {
              const on = roleIds.includes(r.id)
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRoleIds((prev) =>
                    on ? prev.filter((x) => x !== r.id) : [...prev, r.id])}
                  className="w-full px-3 py-2 flex items-center gap-2.5 text-left hover:bg-muted/50"
                >
                  <span className={cn(
                    'h-3.5 w-3.5 shrink-0 border rounded-[3px] inline-flex items-center justify-center',
                    on ? 'bg-chocolate border-chocolate text-chocolate-foreground' : 'border-input',
                  )}>
                    {on && <Check className="h-2.5 w-2.5" />}
                  </span>
                  <span
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ background: r.colour ?? 'var(--color-muted-foreground)' }}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="text-erp-sm font-medium block truncate">{r.displayName}</span>
                    <span className="text-erp-xs text-muted-foreground block truncate">
                      {r.permissionCount} permission{r.permissionCount === 1 ? '' : 's'}
                      {r.description ? ` · ${r.description}` : ''}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </Field>

        <div className="rounded-md border border-border p-3 space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={alsoMobilizer}
              onChange={(e) => setAlsoMobilizer(e.target.checked)}
              className="h-4 w-4 accent-chocolate mt-0.5"
            />
            <span className="text-erp-sm">
              Also add them to the field hierarchy
              <span className="block text-erp-xs text-muted-foreground">
                Creates the mobilizer profile and links it to this account, so the
                same person is one record on the map and in the roster.
              </span>
            </span>
          </label>

          {alsoMobilizer && (
            <div className="grid gap-3 sm:grid-cols-2 pl-6">
              <Field label="Field role" required>
                <select value={mobRole} className="erp-input w-full"
                        onChange={(e) => setMobRole(e.target.value as MobilizerRoleCode)}>
                  {(Object.keys(ROLES) as MobilizerRoleCode[])
                    .filter((r) => r !== 'UNKNOWN')
                    .map((r) => (
                      <option key={r} value={r}>{ROLES[r].label}</option>
                    ))}
                </select>
              </Field>
              <Field
                label="Where they work"
                required
                hint={`A ${ROLES[mobRole].label} sits at ${ROLES[mobRole].shallowestUnit
                  .toLowerCase().replace(/_/g, ' ')} level or below.`}
              >
                {/* NOT NULL on the server — a mobilizer without a unit is a
                    person who appears on no map and in nobody's subtree. */}
                <UnitPicker
                  level={ROLES[mobRole].shallowestUnit}
                  value={mobUnitId}
                  onPick={setMobUnitId}
                />
              </Field>
              <Field label="National ID" hint="Optional. Unique across mobilizers.">
                <input value={mobNationalId} className="erp-input w-full"
                       onChange={(e) => setMobNationalId(e.target.value)} />
              </Field>
            </div>
          )}
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={inviteNow}
            onChange={(e) => setInviteNow(e.target.checked)}
            className="h-4 w-4 accent-chocolate mt-0.5"
          />
          <span className="text-erp-sm">
            Send an invitation now
            <span className="block text-erp-xs text-muted-foreground">
              Without one the account has no password and cannot sign in.
            </span>
          </span>
        </label>
      </form>
    </Modal>
  )
}
